import React, { useState } from "react"

export interface EmbedInstructionsModalProps {
  bandIdentifier: string
  onClose: () => void
}

const DARK_THEME_ATTRS = {
  bg: "#0a0a0a",
  textColor: "#f5f5f5",
  accentColor: "#ffffff",
  mutedColor: "#9ca3af",
}

export const EmbedInstructionsModal: React.FC<EmbedInstructionsModalProps> = ({
  bandIdentifier,
  onClose,
}) => {
  const [copiedId, setCopiedId] = useState<"js" | "iframe" | null>(null)
  const [embedType, setEmbedType] = useState<"js" | "iframe">("js")
  const [theme, setTheme] = useState<"light" | "dark">("light")
  const origin = "https://bandnada.com"

  const jsSnippet =
    theme === "dark"
      ? `<div id="bandnada-concert-embed"></div>
<script
  src="${origin}/embed.js"
  data-band="${bandIdentifier}"
  data-bg="${DARK_THEME_ATTRS.bg}"
  data-text-color="${DARK_THEME_ATTRS.textColor}"
  data-accent-color="${DARK_THEME_ATTRS.accentColor}"
  data-muted-color="${DARK_THEME_ATTRS.mutedColor}"
></script>`
      : `<div id="bandnada-concert-embed"></div>
<script src="${origin}/embed.js" data-band="${bandIdentifier}"></script>`

  const iframeSrc =
    theme === "dark"
      ? `${origin}/b/${encodeURIComponent(bandIdentifier)}/embed/concerts?` +
        `bg=${encodeURIComponent(DARK_THEME_ATTRS.bg)}` +
        `&text_color=${encodeURIComponent(DARK_THEME_ATTRS.textColor)}` +
        `&accent_color=${encodeURIComponent(DARK_THEME_ATTRS.accentColor)}` +
        `&muted_color=${encodeURIComponent(DARK_THEME_ATTRS.mutedColor)}`
      : `${origin}/b/${encodeURIComponent(bandIdentifier)}/embed/concerts`

  const iframeSnippet = `<iframe
  src="${iframeSrc}"
  title="Upcoming shows"
  width="100%"
  height="400"
  style="border: 0;"
></iframe>`

  const copy = (text: string, id: "js" | "iframe") => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopiedId(id)
        setTimeout(() => setCopiedId(null), 2000)
      },
      () => {},
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="embed-instructions-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none"
          aria-label="Close"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className="p-6 overflow-y-auto flex-1 min-h-0 pb-12">
          <h2
            id="embed-instructions-title"
            className="text-xl font-bold text-gray-900 mb-4 pr-8"
          >
            Embed your concert feed
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Add the snippet below to your website. It automatically lists all of
            your published upcoming events which includes events you create, you
            are a collaborator on, or you are listed as an event performer.
          </p>
          <p className="text-sm text-gray-600 mb-6">
            You can see an example of the feed on{" "}
            <a
              href="https://bandnada.com/ewel.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              a demo page here
            </a>
            .
          </p>

          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm font-bold text-gray-700 uppercase">
              Theme
            </span>
            <div
              className="inline-flex rounded border border-gray-300 overflow-hidden"
              role="group"
              aria-label="Embed theme"
            >
              <button
                type="button"
                onClick={() => setTheme("light")}
                aria-pressed={theme === "light"}
                className={`px-3 py-1.5 text-sm font-medium focus:outline-none ${
                  theme === "light"
                    ? "bg-gray-700 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Light
              </button>
              <button
                type="button"
                onClick={() => setTheme("dark")}
                aria-pressed={theme === "dark"}
                className={`px-3 py-1.5 text-sm font-medium border-l border-gray-300 focus:outline-none ${
                  theme === "dark"
                    ? "bg-gray-700 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Dark
              </button>
            </div>
          </div>

          {embedType === "js" ? (
            <section>
              <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">
                JavaScript embed (recommended)
              </h3>
              <div className="relative">
                <pre className="bg-gray-100 border border-gray-200 rounded p-4 text-sm overflow-x-auto pr-24">
                  <code>{jsSnippet}</code>
                </pre>
                <button
                  type="button"
                  onClick={() => copy(jsSnippet, "js")}
                  className="absolute top-2 right-2 px-3 py-1.5 text-xs font-medium bg-gray-700 text-white rounded hover:bg-gray-800 focus:outline-none"
                >
                  {copiedId === "js" ? "Copied!" : "Copy"}
                </button>
              </div>
            </section>
          ) : (
            <section>
              <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">
                Iframe embed
              </h3>
              <div className="relative">
                <pre className="bg-gray-100 border border-gray-200 rounded p-4 text-sm overflow-x-auto pr-24">
                  <code>{iframeSnippet}</code>
                </pre>
                <button
                  type="button"
                  onClick={() => copy(iframeSnippet, "iframe")}
                  className="absolute top-2 right-2 px-3 py-1.5 text-xs font-medium bg-gray-700 text-white rounded hover:bg-gray-800 focus:outline-none"
                >
                  {copiedId === "iframe" ? "Copied!" : "Copy"}
                </button>
              </div>
            </section>
          )}

          <button
            type="button"
            onClick={() => setEmbedType(embedType === "js" ? "iframe" : "js")}
            className="block mt-8 text-sm text-gray-600 hover:text-gray-900 underline focus:outline-none"
          >
            {embedType === "js"
              ? "Use iFrame instead of Javascript"
              : "Use Javascript instead of iframe"}
          </button>
        </div>
      </div>
    </div>
  )
}
